import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DeleteModal } from "../../components";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { MagnifyingGlass, UserPlus, PencilSimple, Trash, UsersThree, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Badge, Skeleton, EmptyState, Pagination, Tooltip,
} from "../../components/ui";

/* ---- avatar ---- */
const AVATAR_COLORS = ["#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];
const colorFor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";

function Users() {
  const { checkAccess } = useAccessChecker();
  const [loading, setLoding] = useState(false);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [done, setDone] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  const debounceRef = useRef(null);

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getUsers();
      setCurrentPage(1);
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const getUsers = async () => {
    try {
      setLoding(true);
      const response = await axiosInstance.get("/users/search", { params: { q: searchValue || "" } });
      const responseData = response.data;
      if (responseData?.data) setUsers(responseData.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load users");
    } finally {
      setLoding(false);
    }
  };

  const filtered = users.filter((u) => {
    const q = (searchValue || "").toLowerCase();
    if (!q) return true;
    return (
      u.fullName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.toLowerCase().includes(q) ||
      u.role?.name?.toLowerCase().includes(q)
    );
  });

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filtered.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.max(1, Math.ceil(filtered.length / usersPerPage));
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Users"
        subtitle="Manage team members, roles and access."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search users…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              containerClassName="w-full sm:w-64"
            />
            {checkAccess("users", "create") && (
              <Link to="/Users/new">
                <Button icon={UserPlus}>New user</Button>
              </Link>
            )}
          </>
        }
      />

      {/* loading skeletons */}
      {loading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-line bg-surface p-5">
              <div className="flex items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-3 w-36" />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="mt-4 h-3.5 w-32" />
            </div>
          ))}
        </div>
      )}

      {/* user cards */}
      {!loading && !!currentUsers.length && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {currentUsers.map((user) => {
            const active = (user.etat || "active").toLowerCase() === "active";
            return (
              <div
                key={user.id}
                className="group flex flex-col rounded-2xl border border-line bg-surface p-5 transition hover:border-line-strong hover:shadow-card"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="grid h-12 w-12 shrink-0 place-items-center rounded-full text-sm font-semibold text-white"
                    style={{ backgroundColor: colorFor(user.fullName || user.email || "?") }}
                  >
                    {initialsOf(user.fullName)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-ink">{user.fullName || "—"}</div>
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-muted">
                      <EnvelopeSimple size={13} className="shrink-0" />
                      <span className="truncate">{user.email || "—"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100">
                    {checkAccess("users", "update") && (
                      <Tooltip label="Edit">
                        <Link to={`/Users/update/${user.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                          <PencilSimple size={16} />
                        </Link>
                      </Tooltip>
                    )}
                    {checkAccess("users", "delete") && (
                      <Tooltip label="Delete">
                        <button
                          onClick={() => {
                            setUserId(user.id);
                            setDeleteModal(true);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"
                        >
                          <Trash size={16} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Badge tone="neutral">{user.role?.name || "No role"}</Badge>
                  <Badge tone={active ? "success" : "neutral"} dot>
                    {active ? "active" : (user.etat || "inactive").toLowerCase()}
                  </Badge>
                </div>

                <div className="mt-3 flex items-center gap-1.5 border-t border-line pt-3 text-sm text-muted">
                  <Phone size={14} className="shrink-0" />
                  <span className="truncate">{user.phone || "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* empty state */}
      {!loading && !currentUsers.length && (
        <EmptyState
          icon={UsersThree}
          title="No users found"
          description="Try a different search, or invite a new team member."
          action={
            checkAccess("users", "create") ? (
              <Link to="/Users/new">
                <Button icon={UserPlus}>New user</Button>
              </Link>
            ) : null
          }
        />
      )}

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={paginate}
        summary={`Showing ${filtered.length ? indexOfFirstUser + 1 : 0}–${Math.min(indexOfLastUser, filtered.length)} of ${filtered.length} users`}
      />

      <DeleteModal
        setDeleteId={setUserId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={userId}
        Model="users"
        setDone={setDone}
        setLoding={setLoding}
        done={done}
      />
    </div>
  );
}

export default Users;
